/**
 * Scroll Reveal Animation Utility
 * Uses Intersection Observer to trigger fade-in animations when elements enter the viewport
 */

// Store observer instance
let observer: IntersectionObserver | null = null

/**
 * Initialize the scroll reveal observer
 */
export function initScrollReveal(): void {
  // Cleanup existing observer
  if (observer) {
    observer.disconnect()
  }

  // Create Intersection Observer
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed')
          // Stop observing after reveal (one-time animation)
          observer?.unobserve(entry.target)
        }
      })
    },
    {
      root: null, // viewport
      rootMargin: '0px 0px -80px 0px', // Trigger slightly before element is fully visible
      threshold: 0.05, // 5% visible
    }
  )

  // Apply to existing elements
  applyScrollReveal()
}

/**
 * Check if a section is a hero section (should be skipped for scroll reveal)
 */
function isHeroSection(section: Element): boolean {
  return (
    section.classList.contains('min-h-screen') ||
    section.classList.contains('min-h-[80vh]') ||
    section.classList.contains('min-h-[60vh]') ||
    !!section.querySelector('#hero-backgrounds')
  )
}

/**
 * Apply scroll-reveal class to eligible elements and observe them
 * Covers: section headers, paragraphs, featured images, carousels, grid/flex card containers, and animate-slide-up elements
 */
export function applyScrollReveal(): void {
  if (!observer) return

  // Get page root
  const pageRoot = document.getElementById('page-root')
  if (!pageRoot) return

  // Find all sections
  const sections = pageRoot.querySelectorAll('section')

  sections.forEach((section) => {
    // Skip hero sections (they have their own entry animations)
    if (isHeroSection(section)) {
      return
    }

    // 1. SectionHeader components (div containers with mb-8/12/16/20 containing h2)
    const sectionHeaders = section.querySelectorAll('.mb-8, .mb-12, .mb-16, .mb-20')
    sectionHeaders.forEach((container) => {
      // Only if it contains an h2 and is a section header container
      const h2 = container.querySelector('h2')
      if (!h2) return
      // Skip if inside a card or grid item
      if (container.closest('.grid > *') || container.closest('.card') || container.closest('.flex-shrink-0')) {
        return
      }
      addScrollReveal(container as HTMLElement, 0)
    })

    // 2. Standalone h2 headers not wrapped in SectionHeader
    const standaloneHeaders = section.querySelectorAll(':scope > div > div > h2, :scope > div > h2')
    standaloneHeaders.forEach((header) => {
      // Skip if parent already has scroll-reveal (SectionHeader case)
      if (header.parentElement?.classList.contains('scroll-reveal')) {
        return
      }
      // Skip if inside a card or grid item
      if (header.closest('.grid > *') || header.closest('.card') || header.closest('.flex-shrink-0')) {
        return
      }
      addScrollReveal(header as HTMLElement, 0)
    })

    // 3. Standalone paragraphs (subtitles) - not inside cards/grids or SectionHeader
    const subtitles = section.querySelectorAll(':scope > div > div > p.text-slate-600, :scope > div > p.text-slate-600, :scope > div > div > div > p.text-slate-600, :scope > div > div > p.text-xl')
    subtitles.forEach((p) => {
      // Skip if parent already has scroll-reveal (SectionHeader case)
      if (p.parentElement?.classList.contains('scroll-reveal')) {
        return
      }
      // Skip if inside a card or grid item
      if (p.closest('.grid > *') || p.closest('.card') || p.closest('.flex-shrink-0') || p.closest('.rounded-xl')) {
        return
      }
      addScrollReveal(p as HTMLElement, 0.1)
    })

    // 4. Featured images (large standalone images like IsoTES product, video containers)
    const featuredImages = section.querySelectorAll('.glass.rounded-3xl')
    featuredImages.forEach((img) => {
      addScrollReveal(img as HTMLElement, 0.2)
    })

    // 5. Carousel items (industry images in horizontal scroll)
    const carousels = section.querySelectorAll('.overflow-x-auto')
    carousels.forEach((carousel) => {
      const items = carousel.querySelectorAll(':scope > div > .flex-shrink-0')
      Array.from(items).forEach((item, index) => {
        addScrollReveal(item as HTMLElement, index * 0.1)
      })
    })

    // 6. Grid children (cards) - animate the whole card, not inner elements
    const grids = section.querySelectorAll('.grid')
    grids.forEach((grid) => {
      const children = grid.children
      Array.from(children).forEach((child, index) => {
        // Skip if already has animate-fade-in-up (handled by component via CSS keyframe)
        if (child.classList.contains('animate-fade-in-up')) {
          return
        }
        // animate-slide-up elements in grids: observe them directly (CSS transition handles reveal)
        if (child.classList.contains('animate-slide-up')) {
          observeSlideUp(child as HTMLElement, index * 0.1)
          return
        }
        addScrollReveal(child as HTMLElement, index * 0.1)
      })
    })

    // 7. animate-slide-up elements NOT in grids (timeline items, standalone cards, flex-based lists)
    const slideUpElements = section.querySelectorAll('.animate-slide-up')
    slideUpElements.forEach((el) => {
      // Skip if already handled above (inside a grid)
      if (el.closest('.grid')) return
      // Find siblings to compute stagger index
      const siblings = el.parentElement ? Array.from(el.parentElement.children) : [el]
      const index = siblings.indexOf(el as HTMLElement)
      observeSlideUp(el as HTMLElement, Math.min(index * 0.1, 0.6))
    })
  })

  // Also handle explicitly marked elements
  document.querySelectorAll('[data-reveal]').forEach((el) => {
    addScrollReveal(el as HTMLElement, 0)
  })
}

/**
 * Observe an animate-slide-up element for scroll reveal (adds revealed class via IntersectionObserver)
 * Does NOT add scroll-reveal class since animate-slide-up already defines the hidden/transition state
 */
function observeSlideUp(element: HTMLElement, delay: number): void {
  // Skip if already revealed or being observed
  if (element.classList.contains('revealed')) return

  // Skip very small elements
  if (element.offsetHeight < 10) return

  // Set stagger delay (overrides any existing animationDelay since we use CSS transition)
  element.style.transitionDelay = `${Math.min(delay, 0.6)}s`

  // Observe the element
  observer?.observe(element)
}

/**
 * Add scroll reveal to an element if not already applied
 */
function addScrollReveal(element: HTMLElement, delay: number): void {
  // Skip if already has scroll-reveal or revealed
  if (element.classList.contains('scroll-reveal') || element.classList.contains('revealed')) {
    return
  }

  // Skip certain elements
  if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE' || element.tagName === 'SVG') {
    return
  }

  // Skip very small elements
  if (element.offsetHeight < 10) {
    return
  }

  // Add scroll-reveal class
  element.classList.add('scroll-reveal')

  // Add stagger delay (max 0.6s)
  element.style.transitionDelay = `${Math.min(delay, 0.6)}s`

  // Observe the element
  observer?.observe(element)
}

/**
 * Observe a specific element for scroll reveal
 */
export function observeElement(element: HTMLElement, delay?: number): void {
  if (!observer) {
    initScrollReveal()
  }

  if (!element.classList.contains('scroll-reveal')) {
    element.classList.add('scroll-reveal')
  }

  if (delay !== undefined) {
    element.style.transitionDelay = `${delay}s`
  }

  observer?.observe(element)
}

/**
 * Observe multiple elements with staggered delays
 */
export function observeElements(elements: HTMLElement[], baseDelay: number = 0.1): void {
  elements.forEach((el, index) => {
    observeElement(el, index * baseDelay)
  })
}

/**
 * Cleanup observer
 */
export function destroyScrollReveal(): void {
  if (observer) {
    observer.disconnect()
    observer = null
  }
}

/**
 * Re-apply scroll reveal after navigation
 * Call this after page content changes
 */
export function refreshScrollReveal(): void {
  // Find new elements and apply scroll reveal
  applyScrollReveal()
}
